import { omit } from 'lodash';

import { Input, Component, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { SnSharePermissions } from '@lib/api/entities';
import { SnFormFieldConfig, SnFormOptions } from '@lib/form';
import { t } from '@lib/translation';

import { SnShareFieldsGeneratorService } from '../../services/share-fields-generator.service';

@Component({
  selector: 'fs-share-invite-form-v2',
  templateUrl: './share-invite-form-v2.component.html',
  styleUrls: ['./share-invite-form-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FsShareInviteFormV2Component implements OnChanges {
  @Input()
  public readonly form = new UntypedFormGroup({});

  @Input()
  public formModel: any = {};

  @Input()
  public additionalFields: SnFormFieldConfig[] = [];

  public formFields: SnFormFieldConfig[];
  public readonly formOptions: SnFormOptions = {};

  constructor(private readonly _shareFieldsGenerator: SnShareFieldsGeneratorService) {
    this.formFields = this._getFormFields();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.additionalFields) {
      this.formFields = this._getFormFields();
    }
  }

  public resetForm(): void {
    this.formOptions.resetModel();
  }

  public getFormData(): Object[] {
    const shares = this.formModel.shares;
    const params = omit(this.formModel, 'shares');

    if (!shares || !Array.isArray(shares) || shares.length === 0) {
      return [];
    }

    return shares.map((organizationId) => {
      return {
        target_organization: organizationId,
        ...params,
      };
    });
  }

  private _getFormFields(): SnFormFieldConfig[] {
    return [
      {
        key: 'permissions',
        defaultValue: SnSharePermissions.View,
        hideExpression: true,
      },
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.shareForm.shareWithOrganization'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'autocomplete-field',
            fieldGroup: [
              this._shareFieldsGenerator.getOrganizationAutocompleteFieldV2({
                className: 'organization-chips',
                templateOptions: {
                  required: true,
                },
              }),
            ],
          },
        ],
      },
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.shareForm.viewAccess'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'view-access-group',
            fieldGroup: [
              this._shareFieldsGenerator.getViewLiveVideoFieldV2(),
              this._shareFieldsGenerator.getViewRecordingsFieldV2(),
              this._shareFieldsGenerator.getRestrictedFieldV2(),
            ],
          },
        ],
      },
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.shareForm.controlAccess'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'control-access-row',
            fieldGroup: [
              this._shareFieldsGenerator.getEnablePTZFieldV2(),
            ],
          },
        ],
      },
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.shareForm.roleAccess'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'role-access-row',
            fieldGroup: [
              this._shareFieldsGenerator.getAdminOnlyFieldV2(),
            ],
          },
        ],
      },
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.shareForm.accessSchedule'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'date-row',
            fieldGroup: [
              this._shareFieldsGenerator.getStartDateField({
                className: 'start-date-col',
                templateOptions: {
                  appearance: 'outline',
                },
              }),
              this._shareFieldsGenerator.getEndDateField({
                className: 'end-date-col',
                templateOptions: {
                  appearance: 'outline',
                },
              }),
            ],
          },
        ],
      },
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.shareForm.dataDisclosure'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'data-disclosure-row',
            fieldGroup: [
              this._shareFieldsGenerator.getDataDisclosureField(),
            ],
          },
        ],
      },
    ];
  }
}
